import { useEffect, memo, useMemo, useState } from "react";
import { db, collection } from "../firebase";
import { getDocs } from "firebase/firestore";
import AOS from 'aos';
import 'aos/dist/aos.css';
import PropTypes from 'prop-types';
import { ArrowOutwardOutlined, AutoAwesomeOutlined, BusinessCenterOutlined, Code, FileCopy, WorkspacePremiumOutlined } from "@mui/icons-material";

// Memoized Components
const Header = memo(() => (
  <div className="text-center mt-4 sm:mt-6 lg:mt-[5%] lg:mb-8 px-[5%]">
    {/* Added mt-4 sm:mt-6 for consistent spacing on mobile/tablet, lg:mt-[5%] kicks in at 1024px */}
    <div className="inline-block relative group">
      <h2
        className="text-3xl md:text-5xl font-bold text-transparent bg-clip-text 
        bg-gradient-to-r from-[#6366f1] to-[#a855f7]"
        data-aos="zoom-in-up"
        data-aos-duration="600"
      >
        About Me
      </h2>
    </div>
    <div
      className="mt-6 text-gray-400 max-w-2xl mx-auto text-base sm:text-md flex items-center justify-center gap-2"
      data-aos="zoom-in-up"
      data-aos-duration="800"
    >
      <div className="relative group">
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#6366f1] to-[#a855f7] rounded-full blur opacity-30 group-hover:opacity-50 transition duration-1000"></div>
        <div className="relative px-3 sm:px-4 py-2 rounded-full bg-black/40 backdrop-blur-xl border border-white/10">
          <span className="bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-transparent bg-clip-text sm:text-sm text-[0.7rem] font-medium flex items-center">
            <AutoAwesomeOutlined className="w-5 h-5 mr-2 text-purple-400" />
            Transforming ideas into digital experiences
            <AutoAwesomeOutlined className="w-5 h-5 ml-2 text-purple-400" />
          </span>
        </div>
      </div>
    </div>
  </div>
));
Header.displayName = "Header";

const ProfileImage = memo(() => (
  <div className="flex justify-end items-center p-2 sm:p-4">
    {/* Simplified padding to p-2 sm:p-4 for consistency, keeps image right-aligned */}
    <div
      className="relative group"
      data-aos="fade-up"
      data-aos-duration="1000"
    >
      <div className="absolute -inset-6 opacity-[25%] z-0 hidden sm:block">
        {/* Hidden on mobile (<640px) for performance, visible on sm+ with gradient effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-indigo-500 to-purple-600 rounded-full blur-2xl animate-spin-slower" />
        <div className="absolute inset-0 bg-gradient-to-l from-fuchsia-500 via-rose-500 to-pink-600 rounded-full blur-2xl animate-pulse-slow opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-600 via-cyan-500 to-teal-400 rounded-full blur-2xl animate-float opacity-50" />
      </div>
      <div className="relative">
        <div className="w-72 h-72 sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-[0_0_40px_rgba(120,119,198,0.3)] transform transition-all duration-700 group-hover:scale-105">
          {/* Kept w-72 h-72 (18rem) for mobile, scales to 20rem on sm+, stays circular with rounded-full */}
          {/* Added max-w-full implicitly via parent flex, but circle shape preserved */}
          <div className="absolute inset-0 border-4 border-white/20 rounded-full z-20 transition-all duration-700 group-hover:border-white/40 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 z-10 transition-opacity duration-700 group-hover:opacity-0 hidden sm:block" />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 via-transparent to-blue-500/20 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 hidden sm:block" />
          <img
            src="/Photo.webp"
            alt="Profile"
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
            loading="lazy"
          />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-700 z-20 hidden sm:block">
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-white/10 to-transparent transform translate-y-full group-hover:-translate-y-full transition-transform duration-1000 delay-100" />
            <div className="absolute inset-0 rounded-full border-8 border-white/10 scale-0 group-hover:scale-100 transition-transform duration-700 animate-pulse-slow" />
          </div>
        </div>
      </div>
    </div>
  </div>
));
ProfileImage.displayName = "ProfileImage";

const StatCard = memo(({ icon: Icon, color, value, label, description, animation, onClick }) => (
  <div onClick={onClick} data-aos={animation} data-aos-duration={1300} className="relative group cursor-pointer">
    <div className="relative z-10 bg-gray-900/50 backdrop-blur-lg rounded-2xl p-6 border border-white/10 overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl group-hover:border-[#6366f1]/50 h-full flex flex-col justify-between">
      <div className={`absolute -z-10 inset-0 bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
      <div className="flex items-center justify-between mb-4">
        <div className="w-16 h-16 rounded-full flex items-center justify-center bg-white/10 transition-transform group-hover:rotate-6">
          <Icon className="w-8 h-8 text-white" />
        </div>
        <span
          className="text-4xl font-bold text-white"
          data-aos="fade-up-left"
          data-aos-duration="1500"
          data-aos-anchor-placement="top-bottom"
        >
          {value}
        </span>
      </div>
      <div>
        <p
          className="text-sm uppercase tracking-wider text-gray-300 mb-2"
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-anchor-placement="top-bottom"
        >
          {label}
        </p>
        <div className="flex items-center justify-between">
          <p
            className="text-xs text-gray-400"
            data-aos="fade-up"
            data-aos-duration="1000"
            data-aos-anchor-placement="top-bottom"
          >
            {description}
          </p>
          <ArrowOutwardOutlined className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
        </div>
      </div>
    </div>
  </div>
));
StatCard.displayName = "StatCard";

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  animation: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const AboutPage = () => {
  const [totals, setTotals] = useState({ totalProjects: 0, totalCertificates: 0 });

  useEffect(() => {
    const readCachedData = (key) => {
      try {
        const localData = localStorage.getItem(key);
        if (localData) return JSON.parse(localData);

        const sessionData = sessionStorage.getItem(key);
        if (sessionData) return JSON.parse(sessionData);

        return [];
      } catch (error) {
        console.error(`Error parsing cached data for ${key}:`, error);
        return [];
      }
    };

    const loadPortfolioCounts = async () => {
      try {
        const storedProjects = readCachedData("projects");
        const storedCertificates = readCachedData("certificates");

        if (storedProjects.length || storedCertificates.length) {
          setTotals({
            totalProjects: storedProjects.length,
            totalCertificates: storedCertificates.length,
          });
          return;
        }

        const [projectSnapshot, certificateSnapshot] = await Promise.all([
          getDocs(collection(db, "projects")),
          getDocs(collection(db, "certificates")),
        ]);

        const projectData = projectSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          TechStack: doc.data().TechStack || [],
        }));

        const certificateData = certificateSnapshot.docs.map((doc) => doc.data());

        localStorage.setItem("projects", JSON.stringify(projectData));
        localStorage.setItem("certificates", JSON.stringify(certificateData));
        sessionStorage.setItem("projects", JSON.stringify(projectData));
        sessionStorage.setItem("certificates", JSON.stringify(certificateData));

        setTotals({
          totalProjects: projectData.length,
          totalCertificates: certificateData.length,
        });
      } catch (error) {
        console.error("Error loading portfolio counts:", error);
        setTotals({ totalProjects: 0, totalCertificates: 0 });
      }
    };

    loadPortfolioCounts();
  }, []);

  const { totalProjects, totalCertificates, YearExperience } = useMemo(() => {
    const startDate = new Date("2023-07-01");
    const today = new Date();
    const experience = today.getFullYear() - startDate.getFullYear() -
      (today < new Date(today.getFullYear(), startDate.getMonth(), startDate.getDate()) ? 1 : 0);

    return {
      totalProjects: totals.totalProjects,
      totalCertificates: totals.totalCertificates,
      YearExperience: experience
    };
  }, [totals]);

  useEffect(() => {
    const initAOS = () => {
      AOS.init({
        once: false,
      });
    };
    initAOS();
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(initAOS, 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const handleStatClick = (label) => {
    const tabMap = {
      "Total Projects": 1,
      Certificates: 2,
      "Years of Experience": 0
    };

    const targetTab = tabMap[label];

    if (targetTab === undefined) return;

    window.dispatchEvent(
      new CustomEvent("portfolio-tab-change", {
        detail: { tab: targetTab },
      })
    );

    const portfolioSection = document.getElementById("Portofolio");
    if (portfolioSection) {
      portfolioSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const statsData = useMemo(() => [
    {
      icon: Code,
      color: "from-[#6366f1] to-[#a855f7]",
      value: totalProjects,
      label: "Total Projects",
      description: "Innovative web solutions crafted",
      animation: "fade-right",
    },
    {
      icon: WorkspacePremiumOutlined,
      color: "from-[#a855f7] to-[#6366f1]",
      value: totalCertificates,
      label: "Certificates",
      description: "Professional skills validated",
      animation: "fade-up",
    },
    {
      icon: BusinessCenterOutlined,
      color: "from-[#6366f1] to-[#a855f7]",
      value: YearExperience,
      label: "Years of Experience",
      description: "Continuous learning journey",
      animation: "fade-left",
    },
  ], [totalProjects, totalCertificates, YearExperience]);

  return (
    <div
      className="h-auto pb-[10%] text-white overflow-hidden px-[5%] sm:px-[5%] lg:px-[10%] mt-10 sm:mt-0"
      id="About"
    >
      {/* Fixed typo: sm-mt-0 -> sm:mt-0; px-[5%] consistent, lg:px-[10%] for wider screens */}
      <Header />
      <div className="w-full mx-auto pt-8 sm:pt-12 relative">
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Mobile: stacks vertically (text below image), lg: two-column grid */}
          <div className="space-y-6 text-center lg:text-left">
            <h2
              className="text-3xl sm:text-4xl lg:text-4xl font-bold"
              data-aos="fade-right"
              data-aos-duration="1000"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7]">
                Hello, I'm
              </span>
              <span
                className="block mt-2 text-gray-200"
                data-aos="fade-right"
                data-aos-duration="1300"
              >
                Qaiser Ejaz
              </span>
            </h2>
            <p
              className="text-base sm:text-lg lg:text-xl text-gray-400 leading-relaxed text-justify pb-4 sm:pb-0"
              data-aos="fade-right"
              data-aos-duration="1500"
            >
              A Computer Systems Engineer with a passion for building enterprise solutions.
              Currently specializing in Microsoft Power Platform and Dynamics 365 Business
              Central, building seamless ERP solutions and engaging user experiences.
              I deliver enterprise-grade customizations across Dynamics 365, automate business
              processes with Power Automate, build interactive dashboards with Power BI, and
              create low-code applications with Power Apps and Power Pages.
            </p>
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4 lg:gap-4 lg:px-0 w-full">
              {/* Buttons stack on mobile, row on lg, full-width on mobile adjusts to content on lg */}
              <a
                href="https://drive.google.com/file/d/13-pu2ByviqqOyVpmoZfqfhe2nd16G9z0/view?usp=sharing"
                className="w-full lg:w-auto"
              >
                <button
                  id="downloadBtn"
                  data-aos="fade-up"
                  data-aos-duration="800"
                  className="w-full lg:w-auto sm:px-6 py-2 sm:py-3 rounded-lg bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center lg:justify-start gap-2 shadow-lg hover:shadow-xl animate-bounce-slow"
                >
                  {/* <FileText className="w-4 h-4 sm:w-5 sm:h-5" /> Download CV */}
                  <FileCopy className="w-4 h-4 sm:w-5 sm:h-5" /> Download CV
                </button>
              </a>
              <a href="#Portofolio" className="w-full lg:w-auto">
                <button
                  id="viewProjectsBtn"
                  data-aos="fade-up"
                  data-aos-duration="1000"
                  className="w-full lg:w-auto sm:px-6 py-2 sm:py-3 rounded-lg border border-[#a855f7]/50 text-[#a855f7] font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center lg:justify-start gap-2 hover:bg-[#a855f7]/10 animate-bounce-slow delay-200"
                >
                  <Code className="w-4 h-4 sm:w-5 sm:h-5" /> View Projects
                </button>
              </a>
            </div>
          </div>
          <ProfileImage />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-16 cursor-pointer">
          {/* Added sm:grid-cols-2 for tablet (640px-768px), md:grid-cols-3 for larger screens */}
          {statsData.map((stat) => (
            <StatCard key={stat.label} {...stat} onClick={() => handleStatClick(stat.label)} />
          ))}
        </div>
      </div>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes spin-slower {
          to { transform: rotate(360deg); }
        }
        .animate-bounce-slow {
          animation: bounce 3s infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s infinite;
        }
        .animate-spin-slower {
          animation: spin-slower 8s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default memo(AboutPage);


